import { ModalContent } from '@creditornot/cb-modal';
import { useMutation } from '@tanstack/react-query';
import React, { useContext } from 'react';
import toast from 'react-hot-toast';
import { useIntl } from 'react-intl';

import { updateOption } from 'apis/restaurant-api/merchant-admin/menu2/api';
import { Option, OptionUpdatePayload } from 'apis/restaurant-api/merchant-admin/menu2/types';
import ErrorToastContent from 'common/components/ErrorToastContent';
import { localize } from 'common/utils';
import { MenuLocaleContext } from 'modules/menu/MenuLocaleContext';
import { reportOptionEditCancel } from 'modules/menu/behavioral';
import MenuEditorSidepanel from 'modules/menu/components/MenuEditorSidepanel';
import useMenuData from 'modules/menu/useMenuData';

import OptionEditor from './OptionEditor';
import { ItemLinks, OptionReadFields } from './OptionView';

type Props = {
  optionState: Option;
  menuId: string;
  onClose: () => void;
};

const OptionSidePanel = ({ optionState, menuId, onClose }: Props) => {
  const intl = useIntl();
  const { updateMenuData } = useMenuData();

  const { primaryLanguage } = useContext(MenuLocaleContext);

  const { mutateAsync: handleSubmit, isLoading: submitting } = useMutation(
    (payload: OptionUpdatePayload) => updateOption(menuId, payload.id, payload),
    {
      onError: (error: any) => {
        toast.error(<ErrorToastContent error={error} />, { duration: Infinity });
      },
      onSuccess: result => {
        updateMenuData({ actionType: 'updateOption', payload: result });
      },
    },
  );

  const optionName = localize(optionState.name, intl.locale);

  return (
    <MenuEditorSidepanel id={optionState.id} title={optionName} onClose={onClose}>
      {({ editing, setEditing }) => (
        <ModalContent style={{ paddingBlockStart: 0 }}>
          {editing ? (
            <OptionEditor
              // TODO: figure out option error state
              error={null}
              language={primaryLanguage}
              menuId={menuId}
              onCancel={() => {
                setEditing(false);
                reportOptionEditCancel();
              }}
              onSubmit={async payload => {
                await handleSubmit(payload);
                setEditing(false);
              }}
              option={optionState}
              submitting={submitting}
            />
          ) : (
            <>
              <OptionReadFields language={primaryLanguage} option={optionState} />
              <ItemLinks language={primaryLanguage} optionId={optionState.id} />
            </>
          )}
        </ModalContent>
      )}
    </MenuEditorSidepanel>
  );
};

export default OptionSidePanel;
